import { FC } from 'react';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

export const DEFAULT_ZOOM_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];

interface ZoomControlsProps {
  zoom: number;
  onChange: (zoom: number) => void;
  presets?: number[];
  /**
   * Render as a flat row instead of the floating document overlay.
   *
   * The default variant is `position: absolute` in the document's bottom-right
   * corner. A surface that puts its actions there (the renter-document review)
   * has to own that corner, so it asks for `inline` and hosts the control in
   * its header — zoom is dialog chrome, not document content.
   */
  inline?: boolean;
}

const ZoomControls: FC<ZoomControlsProps> = ({
  zoom,
  onChange,
  presets = DEFAULT_ZOOM_PRESETS,
  inline = false,
}) => {
  const zoomMin = presets[0];
  const zoomMax = presets[presets.length - 1];

  const handleZoomIn = (): void => {
    const nextIndex = presets.findIndex((p) => p > zoom);
    onChange(nextIndex !== -1 ? presets[nextIndex] : zoomMax);
  };

  const handleZoomOut = (): void => {
    const reversed = [...presets].reverse();
    const nextIndex = reversed.findIndex((p) => p < zoom);
    onChange(nextIndex !== -1 ? reversed[nextIndex] : zoomMin);
  };

  const handleZoomSelect = (event: SelectChangeEvent<number>): void => {
    onChange(event.target.value as number);
  };

  const iconSize = inline ? 20 : 22;

  const zoomInButton = (
    <IconButton
      onClick={handleZoomIn}
      aria-label="Zoom in"
      disabled={zoom >= zoomMax}
      size="small"
    >
      <ZoomInIcon sx={{ fontSize: iconSize }} />
    </IconButton>
  );

  const zoomOutButton = (
    <IconButton
      onClick={handleZoomOut}
      aria-label="Zoom out"
      disabled={zoom <= zoomMin}
      size="small"
    >
      <ZoomOutIcon sx={{ fontSize: iconSize }} />
    </IconButton>
  );

  const zoomLevelSelect = (
    <FormControl size="small" sx={{ minWidth: inline ? 72 : 80 }}>
      <Select<number>
        value={zoom}
        onChange={handleZoomSelect}
        inputProps={{ 'aria-label': 'Zoom level' }}
        sx={{ fontSize: 13, fontWeight: 600 }}
      >
        {presets.map((preset) => (
          <MenuItem key={preset} value={preset}>
            {Math.round(preset * 100)}%
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (inline) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        data-testid="zoom-controls-inline"
      >
        {zoomOutButton}
        {zoomLevelSelect}
        {zoomInButton}
      </Stack>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        bottom: 12,
        right: 12,
        zIndex: 10,
        borderRadius: '12px',
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(6px)',
        boxShadow: '0px 4px 16px rgba(0,0,0,0.40)',
      }}
    >
      <Stack direction="column" alignItems="center" sx={{ p: 0.5, gap: 0.25 }}>
        {zoomInButton}
        {zoomLevelSelect}
        {zoomOutButton}
      </Stack>
    </Paper>
  );
};

export default ZoomControls;
